import { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { fromApiHms, toApiYmd } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { yesNo, interviewStatus } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import { interviewReservationDeleteUrl, interviewReservationSelectionUpdateUrl } from '../common/constant/appUrl'
import { useErrorHandle } from '../common/error/errorHandler'
import { useOperationLog } from '../common/operationLog'
import { Interview, FacilityInputDetail, getInterview } from '../common/interview'
import { getFacility } from '../common/facility'
import { showLoading } from '../common/store/slices/application'
import { GetProjectDetailDto } from '../../dataAccess/webApi/dto/projectsDto'
import { TEMP_FILE_CONDITION } from '../common/constant/projectMst'
import { executeGetProjectDetail } from '../../dataAccess/webApi/dao/projectsDao'

interface UrlParams {
  interviewNo: string
}

type InterviewDetail = {
  cancelLimit?: number
  cancelLimitTime?: Date
  cancelNote?: string
  cancelExcludeHolidayFlag?: string
  cancelLimitHolidayOption?: string
  immediatelyReservationFlag?: string
  isInterviewDocsFlagOn: boolean
  facilityInputFields?: { [key: string]: FacilityInputDetail }
} & Interview

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { interviewNo } = useParams<UrlParams>()
  const { addOperationLog, attachAccessData } = useOperationLog()

  const [interviewDetail, setInterviewDetail] = useState<InterviewDetail>()

  // 初期表示
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })

    dispatch(
      showLoading(
        errorHandle(async () => {
          const result = await getInterviewDetail(interviewNo)
          attachAccessData({ accessData: [{ userIdRegFlag: yesNo.yes, childId: result.childId, interviewNo }] })

          setInterviewDetail(result)
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // キャンセルボタン押下
  const goCancel = useCallback(
    (status: string) => {
      if (status === interviewStatus.wait) {
        addOperationLog({ operationId: OperationId.OP_00000032 })
      } else {
        addOperationLog({ operationId: OperationId.OP_00000031 })
      }

      history.push(interviewReservationDeleteUrl.url(interviewNo))
    },
    [history, interviewNo, addOperationLog]
  )

  // 変更ボタン押下
  const goChange = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000035 })

    history.push(interviewReservationSelectionUpdateUrl.url(interviewNo))
  }, [history, interviewNo, addOperationLog])

  return {
    interviewDetail,
    goCancel,
    goChange
  }
}

/**
 * 面談情報取得
 */
const getInterviewDetail = async (interviewNo: string) => {
  const interview = await getInterview(interviewNo)
  const facility = await getFacility(interview.facilityId, interview.interviewDate)
  const projectMstData: GetProjectDetailDto = (await executeGetProjectDetail(facility.projectId, toApiYmd(Date.now()))).result

  return nullPropsToUndefined({
    ...interview,
    cancelLimit: facility.interviewCancelLimit,
    cancelLimitTime: fromApiHms(facility.interviewCancelLimitTime),
    cancelNote: facility.interviewCancelNote,
    cancelExcludeHolidayFlag: facility.interviewCancelExcludeHolidayFlag,
    cancelLimitHolidayOption: facility.interviewCancelLimitHolidayOption,
    immediatelyReservationFlag: facility.interviewImmediatelyReservationFlag,
    isInterviewDocsFlagOn: projectMstData.interviewDocFlag === TEMP_FILE_CONDITION.USED_BOTH,
    ...createFacilityInputFields(facility, interview),
  })
}

/**
 * 施設別面談時入力項目
 */
const createFacilityInputFields = (facility?: any, interview?: any) => {
  const facilityInputFields:{ [key: string]: FacilityInputDetail } = {}

  if (facility && facility.interviewFacilityInputFieldsDisplayFlag === yesNo.yes) {
    // 任意項目の初期値を設定
    facility.interviewFacilityInputFields?.forEach((field: any) => {
      const key = field.id
      facilityInputFields[String(key)] = {
        id: null,
        answer: "",
        updateDatetime: null,
        facilityInterviewInputFieldId: key,
        order: field.order,
      }
    })

    // 回答情報がある場合は書き換える
    if (interview) {
      interview.facilityInputAnswers?.forEach((answer: any) => {
        const key = answer.facilityInterviewInputFieldId
        if (facilityInputFields[String(key)]) {
          facilityInputFields[key] = {
            id: answer.id,
            answer: answer.answer,
            updateDatetime: answer.updateDatetime,
            facilityInterviewInputFieldId: key,
            order: facilityInputFields[key].order
          }
        }
      })
    }
  }

  return {
    facilityInputFields
  }
}
